import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { getProspectSearchBootstrap } from "@/app/actions/prospect-search/bootstrap/action";
import { ProspectSearchBootstrap } from "@/app/types/prospect/ProspectSearchBootstrap";
import { UCookie } from "@/app/utils/UCookie";
import { QueryKey } from "@/app/vars/QueryKeys";
import { CookieKey } from "@/app/vars/Storage";

type Options = {
  projectId?: number;
  enabled?: boolean;
  initialData?: ProspectSearchBootstrap;
};

// Collapses the offers -> avatars -> workspace chain into one round-trip and seeds every
// downstream cache, so the page's initial load is a single Server Action instead of three
// serialized ones. The individual hooks stay gated on this and read the seeded caches;
// offer/avatar switches afterwards flow through them as usual.
export function useFetchProspectSearchBootstrap(options: Options) {
  const { projectId, enabled = true, initialData } = options;
  const queryClient = useQueryClient();

  const seedCaches = (bootstrap: ProspectSearchBootstrap) => {
    queryClient.setQueryData([QueryKey.PROJECT_OFFER, projectId], bootstrap.offers);
    queryClient.setQueryData([QueryKey.PROSPECT_ACTION_TYPES, projectId], bootstrap.actionTypes);

    if (bootstrap.selectedOfferId) {
      queryClient.setQueryData(
        [QueryKey.CLIENT_AVATAR, bootstrap.selectedOfferId],
        bootstrap.avatars,
      );
    }

    const avatarId = bootstrap.selectedAvatarId;
    const workspace = bootstrap.workspace;
    if (avatarId && workspace) {
      queryClient.setQueryData([QueryKey.PROSPECT_SEARCH_WORKSPACE, avatarId], workspace);
      queryClient.setQueryData([QueryKey.PROSPECT_SEARCH_SUMMARY, avatarId], workspace.summary);
      queryClient.setQueryData([QueryKey.PROSPECTING_STATS, avatarId], workspace.stats);
      queryClient.setQueryData(
        [QueryKey.SOCIAL_IMPORTED_PROFILE_COUNTS, avatarId],
        workspace.importedCounts,
      );
    }
  };

  const seededProjectId = useRef<number | undefined>(undefined);
  if (initialData && projectId && seededProjectId.current !== projectId) {
    seedCaches(initialData);
    seededProjectId.current = projectId;
  }

  return useQuery({
    queryKey: [QueryKey.PROSPECT_SEARCH_BOOTSTRAP, projectId],
    queryFn: async () => {
      const hintOfferId = UCookie.readNumber(CookieKey.SELECTED_OFFER);
      const hintAvatarId = UCookie.readNumber(CookieKey.SELECTED_AVATAR);

      const bootstrap = await getProspectSearchBootstrap(projectId!, hintOfferId, hintAvatarId);
      seedCaches(bootstrap);

      return bootstrap;
    },
    enabled: enabled && !!projectId,
    staleTime: Infinity,
    initialData: seededProjectId.current === projectId ? initialData : undefined,
  });
}
